import { conversationsApi } from "@/services/conversation-service";
import { Conversations } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { isToday, isYesterday, startOfDay, subDays } from "date-fns";
import { useLayoutEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PlaygroundListItem } from "./playground-list-item";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { usePlaygroundRouting } from "../routing/playground-routing";
import { useConversationScope } from "@/hooks/useConversationScope";
import { conversationQueryKeys, conversationScopeIdentity } from "@/features/playground/conversation-query-keys";
import { HistoryFocusBoundary } from "./history-focus";
import { usePlaygroundPresentation } from "./playground-presentation";

type ConversationGroupKey = "today" | "yesterday" | "last7Days" | "older";

const groupConversationsByDate = async (
  search: string,
  requestScope: { organizationId: string | null; projectId: string | null },
): Promise<Record<ConversationGroupKey, Conversations>> => {
  const data = await conversationsApi.getConversations({
    search: search,
    include_archived: false,
    skip: 0,
    limit: 50,
    requestScope,
  });
  const now = new Date();
  const startOfYesterday = startOfDay(subDays(now, 1));
  const sevenDaysAgo = startOfDay(subDays(now, 7));
  const groupedDataByDate = data.reduce(
    (acc, current) => {
      const updated_at = new Date(current.updated_at);
      if (isToday(updated_at)) {
        acc.today.push(current);
      } else if (isYesterday(updated_at)) {
        acc.yesterday.push(current);
      } else if (updated_at >= sevenDaysAgo && updated_at < startOfYesterday)
        acc.last7Days.push(current);
      else acc.older.push(current);

      return acc;
    },
    {
      today: [],
      yesterday: [],
      last7Days: [],
      older: [],
    },
  );
  return groupedDataByDate;
};

export const PlaygroundConversations = ({ search }: { search: string }) => {
  const queryScope = useConversationScope();
  // A fresh owner for each scope/search/bootstrap session prevents callbacks
  // retained from an earlier A -> B -> A screen from retrying its old query.
  const sessionKey = JSON.stringify([conversationScopeIdentity(queryScope.scope), search, queryScope.ready]);
  return <HistoryFocusBoundary scopeKey={sessionKey}><ScopedHistory key={sessionKey} search={search} queryScope={queryScope} /></HistoryFocusBoundary>;
};

const ScopedHistory = ({ search, queryScope }: {
  search: string;
  queryScope: ReturnType<typeof useConversationScope>;
}) => {
  const { t } = useTranslation();
  const { modern } = usePlaygroundPresentation();
  const { id: activeConversationId } = usePlaygroundRouting();
  const { scope, requestScope, ready } = queryScope;
  const active = useRef(false);
  const retrying = useRef(false);
  useLayoutEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: conversationQueryKeys.history(scope, search),
    queryFn: () => groupConversationsByDate(search, requestScope),
    enabled: ready,
    staleTime: Infinity,
  });
  const handleRetry = async () => {
    if (!active.current || !ready || isFetching || retrying.current) return;
    retrying.current = true;
    try {
      await refetch({ cancelRefetch: false });
    } finally {
      retrying.current = false;
    }
  };
  if (!ready || (isPending && !data))
    return (
      <div role="status" aria-label={t("common.loading")} className="py-4 flex flex-col gap-3">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="rounded-lg p-2 motion-reduce:animate-none" />
        ))}
      </div>
    );
  return (
    <div
      style={{
        padding: modern ? undefined : "16px 0",
      }}
      className={modern ? "flex flex-col gap-4" : "flex flex-col gap-3"}
      aria-busy={isFetching}
    >
      {isError && (
        <div className="flex flex-col items-start gap-2 px-2">
          <p role="alert" className="text-sm text-destructive dark:text-destructive-foreground">{t("playground.historyLoadError")}</p>
          <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={handleRetry}>
            {t("common.retry")}
          </Button>
        </div>
      )}
      {!isError && data && Object.values(data).every((group) => group.length === 0) && (
        <p className="px-2 text-sm text-muted-foreground">{t(modern && search ? "conversation.noMatchingConversations" : "conversation.noConversationsYet")}</p>
      )}
      {data &&
        Object.entries(data).filter(([, group]) => !modern || group.length > 0).map((conversationEntry) => (
          <div key={conversationEntry[0]} className={modern ? "flex min-w-0 flex-col gap-1" : "flex flex-col gap-2"}>
            {conversationEntry[1].length !== 0 && (
              <span className={modern ? "px-2 py-0.5 text-[12.8px] font-bold leading-[19.2px] text-foreground/70" : "text-[14.8px] text-foreground opacity-70 font-bold pt-1 pr-0 pb-1 pl-2 rtl:pr-2 rtl:pl-0"}>
                {t(`conversation.${conversationEntry[0]}`)}
              </span>
            )}
            {conversationEntry[1].map((conversation) => (
              <PlaygroundListItem
                title={conversation.name}
                id={conversation.id}
                key={conversation.id}
                active={conversation.id === activeConversationId}
              />
            ))}
          </div>
        ))}
    </div>
  );
};
